import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, X } from 'lucide-react';
import { useAppSettings } from '../components/providers/AppSettingsProvider';
import { getPageBackgroundGradient } from '../config/theme';
import EmotionPieChart, { getEmotionColor } from '../components/EmotionPieChart';
import type { EmotionSlice } from '../components/EmotionPieChart';
import type { DetectedEmotion, StoredConversation, DailySummary } from '../services/journalService';
import { isValidUUID } from '../utils';
import { Capacitor } from '@capacitor/core';

const APP_STORE_URL = 'https://apps.apple.com/us/app/call-lumi-voice-companion/id6753812745';

function AppDownloadModal({ onClose, t }: { onClose: () => void; t: (key: string) => string }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 pb-10"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-3xl overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #1c1c2e 0%, #16213e 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 pt-5">
          <div className="flex justify-end mb-1">
            <button onClick={onClose} className="text-white/30 hover:text-white/60 transition-colors p-1">
              <X size={18} />
            </button>
          </div>
          <h2 className="text-white text-xl font-semibold text-center mb-5">
            {t('appDownload.title')}
          </h2>
          <div className="space-y-3 mb-6 text-sm text-white/65">
            <div className="flex items-center gap-3">
              <span className="text-base">🎙️</span>
              <span>{t('appDownload.feature1')}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-base">🔔</span>
              <span>{t('appDownload.feature2')}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-base">🔒</span>
              <span>{t('appDownload.feature3')}</span>
            </div>
          </div>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-3.5 rounded-2xl font-semibold text-white transition-opacity hover:opacity-90 active:opacity-75"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            onClick={onClose}
          >
            {t('appDownload.downloadBtn')}
          </a>
          <button
            onClick={onClose}
            className="block w-full text-center py-3 text-white/35 text-sm mt-1 hover:text-white/55 transition-colors"
          >
            {t('appDownload.later')}
          </button>
        </div>
      </div>
    </div>
  );
}

type EmotionRecord = {
  emotion: string;
  emoji: string;
  label: string;
  reflection?: string;
  timestamp: number;
  conversationId?: string;
};

type DetailPageState = {
  dateKey: string;
  dateLabel: string;
  conversations: StoredConversation[];
  emotions: EmotionRecord[];
  existingSummary?: DailySummary | {
    title: string;
    primaryEmotion: { emoji: string; label: string };
    lumiHeard: string;
    detectedEmotions: DetectedEmotion[];
  };
  conversationId?: string;
  personaId?: string;
};

type DetailData = {
  title: string;
  primaryEmotion: { emoji: string; label: string };
  lumiHeard: string;
  detectedEmotions: DetectedEmotion[];
};

function isValidDetailPageState(s: unknown): s is DetailPageState {
  if (!s || typeof s !== 'object') return false;
  const obj = s as Record<string, unknown>;
  return (
    typeof obj.dateKey === 'string' &&
    typeof obj.dateLabel === 'string' &&
    Array.isArray(obj.conversations)
  );
}

const formatFullDate = (dateKey: string): string => {
  const date = new Date(dateKey + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (timestamp: number): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${hours}:${minutes}${ampm}`;
};

function toEmotionSlices(emotions: DetectedEmotion[]): EmotionSlice[] {
  return emotions.map((e, i) => ({
    emotion: e.emotion,
    percentage: e.percentage,
    color: getEmotionColor(e.emotion, i),
  }));
}

function getLatestConversationTimestamp(conversations: StoredConversation[]): number {
  let latest = 0;
  conversations.forEach((conv) => {
    latest = Math.max(latest, conv.updatedAt || conv.createdAt || 0);
    conv.messages?.forEach((msg) => {
      if (msg?.ts && msg.ts > latest) {
        latest = msg.ts;
      }
    });
  });
  return latest;
}

const ConversationSummaryDetail: React.FC = () => {
  const { t, theme } = useAppSettings();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();

  const rawState = location.state;
  const state = isValidDetailPageState(rawState) ? rawState : null;

  const [activeTab, setActiveTab] = useState<'analysis' | 'transcript'>('analysis');
  const [detail, setDetail] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [showAppDownload, setShowAppDownload] = useState(false);

  // 仅 Web 端：每次查看对话总结后弹出 App 下载引导
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'web') return;
    const timer = setTimeout(() => {
      setShowAppDownload(true);
    }, 2000); // 2秒后弹出，让用户先看到总结内容
    return () => clearTimeout(timer);
  }, []);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!state) {
      navigate('/journals', { replace: true });
    }
  }, [state, navigate]);

  useEffect(() => {
    if (!state) return;
    setDetail(null);
    setError(false);
    setLoading(true);
    fetchingRef.current = false;
  }, [state?.dateKey, state?.conversationId]);

  const fetchDetail = useCallback(async () => {
    if (!state || fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(false);
    setDetail(null);

    try {
      // 1. If we have an existingSummary from navigation state, use it immediately
      const existing = state.existingSummary;
      if (existing && ('title' in existing) && existing.title) {
        const summary = existing as { title: string; primaryEmotion?: { emoji: string; label: string }; lumiHeard?: string; summary?: string; detectedEmotions?: DetectedEmotion[] };
        setDetail({
          title: summary.title,
          primaryEmotion: summary.primaryEmotion || { emoji: '', label: '' },
          lumiHeard: summary.lumiHeard || (summary as any).summary || '',
          detectedEmotions: summary.detectedEmotions || [],
        });
        setLoading(false);
        return;
      }

      // 2. For conversation_auto items, poll the check-in API
      const conversationId = state.conversationId;
      if (!conversationId || !isValidUUID(conversationId)) {
        setError(true);
        setLoading(false);
        return;
      }

      const { fetchCheckInByConversation } = await import('../services/exploreService');

      // Poll: the backend summarizer runs async, so the check-in may not have full data yet
      const poll = async (attempts = 10, delayMs = 1000): Promise<DetailData | null> => {
        for (let i = 0; i < attempts; i++) {
          const item = await fetchCheckInByConversation(conversationId);
          if (item && item.journal_title) {
            return {
              title: item.journal_title,
              primaryEmotion: { emoji: item.emoji, label: item.emotion_tag },
              lumiHeard: item.journal_text || '',
              detectedEmotions: (item.detected_emotions || []).map((e) => ({
                emotion: e.emotion,
                percentage: e.percentage,
                color: e.color,
              })),
            };
          }
          if (i < attempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
        return null;
      };

      const result = await poll();
      if (result) {
        setDetail(result);
      } else {
        // No data after polling — conversation may have been filtered as not meaningful
        setShowSkipped(true);
        await new Promise((resolve) => setTimeout(resolve, 2500));
        navigate('/whisper', { replace: true });
        return;
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [state, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleRetry = () => {
    fetchingRef.current = false;
    fetchDetail();
  };

  if (!state) return null;

  if (showSkipped) {
    return (
      <div className={`fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-b ${getPageBackgroundGradient(theme)}`}>
        <div className="mb-6">
          <span className="text-5xl">✨</span>
        </div>
        <p className="text-lg font-semibold mb-3 text-white text-center px-8">
          {t('whisper.journalSkipped.title')}
        </p>
        <p className="text-sm text-center px-10 text-white/60 leading-relaxed">
          {t('whisper.journalSkipped.subtitle')}
        </p>
      </div>
    );
  }

  const emotionSlices = detail ? toEmotionSlices(detail.detectedEmotions) : [];
  const conversationTimestamp = getLatestConversationTimestamp(state.conversations);

  return (
    <div className="fixed inset-0 w-full h-screen overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />

      <div className="relative z-10 h-full flex flex-col">
        {/* Analysis / Transcript Tab */}
        <div className="flex justify-center px-5 flex-shrink-0" style={{ paddingTop: '10vh' }}>
          <div
            className="flex rounded-full p-1"
            style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
          >
            {(['analysis', 'transcript'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeTab === tab
                    ? 'bg-white/20 text-white'
                    : 'text-white/50'
                }`}
              >
                {t(`journalDetail.${tab}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Date + Time Header */}
        <div className="flex items-center gap-2 px-4 py-4 flex-shrink-0">
          <button
            onClick={() => navigate('/journals')}
            className={`p-2 rounded-xl transition-colors flex-shrink-0 ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-gray-800'}`}
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {formatFullDate(state.dateKey)}
          </h1>
          {conversationTimestamp > 0 && (
            <span className="text-sm text-white/50 font-normal">
              {formatTime(conversationTimestamp)}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-20">
          {activeTab === 'analysis' ? (
            loading ? (
              <LoadingSkeleton isDark={isDark} t={t} />
            ) : error ? (
              <ErrorState isDark={isDark} t={t} onRetry={handleRetry} />
            ) : detail ? (
              <div className="space-y-8">
                {/* Title Section */}
                <div>
                  <h2 className={`text-xl font-semibold leading-snug mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {detail.title}
                  </h2>

                  {detail.primaryEmotion.emoji && (
                    <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium ${isDark ? 'bg-white/15 text-white' : 'bg-white/70 text-gray-800'}`}>
                      <span className="text-base">{detail.primaryEmotion.emoji}</span>
                      {detail.primaryEmotion.label}
                    </span>
                  )}
                </div>

                {/* Lumi Heard Section */}
                <div>
                  <h3 className={`text-base font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t('journalDetail.lumiHeard') || 'Lumi Heard'}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-white/85' : 'text-gray-700'}`}>
                    {detail.lumiHeard}
                  </p>
                </div>

                {/* Lumi Detected Section */}
                {emotionSlices.length > 0 && (
                  <div>
                    <h3 className={`text-base font-semibold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {t('journalDetail.lumiDetected') || 'Lumi Detected'}
                    </h3>
                    <EmotionPieChart
                      emotions={emotionSlices}
                      size={180}
                      isDark={isDark}
                    />
                  </div>
                )}
              </div>
            ) : null
          ) : (
            /* Transcript Tab */
            <TranscriptView
              conversations={state.conversations}
              conversationId={state.conversationId}
              isDark={isDark}
            />
          )}
        </div>
      </div>

      {showAppDownload && (
        <AppDownloadModal onClose={() => setShowAppDownload(false)} t={t} />
      )}
    </div>
  );
};

const LoadingSkeleton: React.FC<{ isDark: boolean; t: (key: string) => string }> = ({ isDark, t }) => (
  <div className="space-y-8">
    <div>
      <div className={`h-6 rounded-lg w-4/5 mb-3 animate-pulse ${isDark ? 'bg-white/20' : 'bg-gray-200'}`} />
      <div className={`h-6 rounded-lg w-3/5 mb-4 animate-pulse ${isDark ? 'bg-white/20' : 'bg-gray-200'}`} />
      <div className={`h-8 rounded-full w-32 animate-pulse ${isDark ? 'bg-white/15' : 'bg-gray-200'}`} />
    </div>
    <div>
      <div className={`h-5 rounded-lg w-28 mb-3 animate-pulse ${isDark ? 'bg-white/20' : 'bg-gray-200'}`} />
      <div className="space-y-2">
        <div className={`h-4 rounded w-full animate-pulse ${isDark ? 'bg-white/15' : 'bg-gray-200'}`} />
        <div className={`h-4 rounded w-full animate-pulse ${isDark ? 'bg-white/15' : 'bg-gray-200'}`} />
        <div className={`h-4 rounded w-3/4 animate-pulse ${isDark ? 'bg-white/15' : 'bg-gray-200'}`} />
      </div>
    </div>
    <div>
      <div className={`h-5 rounded-lg w-32 mb-5 animate-pulse ${isDark ? 'bg-white/20' : 'bg-gray-200'}`} />
      <div className="flex justify-center">
        <div className={`w-[180px] h-[180px] rounded-full animate-pulse ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
      </div>
    </div>
    <p className={`text-center text-sm mt-6 ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
      {t('journalDetail.loading') || 'Reflecting on your conversation...'}
    </p>
  </div>
);

const ErrorState: React.FC<{ isDark: boolean; t: (key: string) => string; onRetry: () => void }> = ({ isDark, t, onRetry }) => (
  <div className="text-center py-8">
    <p className={`text-sm mb-4 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
      {t('journalDetail.error') || 'Unable to generate your reflection right now.'}
    </p>
    <button
      onClick={onRetry}
      className={`px-5 py-2 rounded-xl text-sm font-medium transition-colors border ${isDark
        ? 'bg-white/20 hover:bg-white/30 text-white border-white/30'
        : 'bg-white/60 hover:bg-white/80 text-gray-800 border-white/70'
      }`}
    >
      {t('journalDetail.retry') || 'Try again'}
    </button>
  </div>
);

const TranscriptView: React.FC<{
  conversations: StoredConversation[];
  conversationId?: string;
  isDark: boolean;
}> = ({ conversations, conversationId, isDark }) => {
  const [turns, setTurns] = React.useState<
    { speaker: string; text: string; timestamp: string }[] | null
  >(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (conversationId && isValidUUID(conversationId)) {
        try {
          const { fetchTranscript } = await import('../services/exploreService');
          const data = await fetchTranscript(conversationId);
          if (!cancelled && data && data.turns.length > 0) {
            setTurns(data.turns.map((t) => ({
              speaker: t.speaker,
              text: t.text,
              timestamp: t.timestamp,
            })));
            setLoading(false);
            return;
          }
        } catch {
          // fallback to local data below
        }
      }

      if (!cancelled) {
        const localMessages = conversations
          .flatMap((conv) => conv.messages || [])
          .filter((msg) => msg?.text?.trim())
          .sort((a, b) => (a.ts || 0) - (b.ts || 0))
          .map((msg) => ({
            speaker: msg.role === 'user' ? 'user' : 'lumi',
            text: msg.text,
            timestamp: msg.ts > 0 ? new Date(msg.ts).toISOString() : '',
          }));
        setTurns(localMessages);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, conversations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-pulse text-white/40 text-sm">Loading transcript...</div>
      </div>
    );
  }

  if (!turns || turns.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
          No transcript available
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {turns.map((msg, i) => {
        const isUser = msg.speaker === 'user';
        return (
          <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                isUser
                  ? 'bg-white/20 text-white rounded-br-sm'
                  : isDark
                    ? 'bg-white/10 text-white/85 rounded-bl-sm'
                    : 'bg-white/40 text-gray-800 rounded-bl-sm'
              }`}
            >
              <p>{msg.text}</p>
              {msg.timestamp && (
                <p className={`text-[10px] mt-1 ${isUser ? 'text-white/50 text-right' : 'text-white/40'}`}>
                  {formatTime(new Date(msg.timestamp).getTime())}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ConversationSummaryDetail;
